import { useAppDispatch } from '@/app/redux/hooks';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import { ContentPanelTabsTrigger } from '@/common/ui/ContentPanel';
import { useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import { ProIcon } from '@/common/ui/UpgradeButton';
import CustomLevelEditorTour from '@/dashboard/CustomLevel/CustomLevelEditorTour';
import ControlPanel from '@/dashboard/LessonPlan/ControlPanel';
import LessonPlanEditor from '@/dashboard/LessonPlan/LessonPlanEditor';
import {
  BookOpenTextIcon,
  JoystickIcon,
  NotebookIcon,
  PresentationIcon,
} from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList } from '@shadcn/components/tabs';
import { useState } from 'react';
import styled from 'styled-components';
import LessonPlanGameLevel from './LessonPlanGameLevel';
import Presenter from './Presenter/Presenter';
import WorksheetEditor from './WorksheetEditor';

interface Props {
  lessonPlanId?: string;
  lessonPlanName?: string;
  content: string;
  onContentChange: (content: string) => void;
  worksheetContent: string;
  onWorksheetContentChange: (content: string) => void;
  worksheetOutOfSync: boolean;
  onWorksheetRegenerate: () => void;
  gameLevelOutOfSync: boolean;
  onGameLevelSync: () => void;
  setIsGameLevelSaving: (isSaving: boolean) => void;
  setGameLevelLastSaved: (lastSaved: number | undefined) => void;
  selectedSectionId: string | null;
  onSelectedSectionIdChange: (id: string | null) => void;
}

export default function LessonPlan({
  lessonPlanId,
  lessonPlanName,
  content,
  onContentChange,
  worksheetContent,
  onWorksheetContentChange,
  worksheetOutOfSync,
  onWorksheetRegenerate,
  gameLevelOutOfSync,
  onGameLevelSync,
  setIsGameLevelSaving,
  setGameLevelLastSaved,
  selectedSectionId,
  onSelectedSectionIdChange,
}: Props) {
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useIsFeatureEnabled();
  const isCustomLevelsEnabled = isFeatureEnabled('custom-levels');

  const [activeTab, setActiveTab] = useState<
    'lesson-plan' | 'worksheet' | 'present' | 'game-level'
  >('lesson-plan');

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => {
        if (v === 'game-level' && !isCustomLevelsEnabled) {
          dispatch(setAttemptedProFeature('Custom Levels'));
          return;
        }
        setActiveTab(
          v as 'lesson-plan' | 'worksheet' | 'present' | 'game-level',
        );
      }}
      className="flex flex-col border-b bg-white h-full overflow-hidden"
    >
      <TabsListContainer className="relative">
        <TabsList className="bg-transparent gap-1">
          <ContentPanelTabsTrigger value="lesson-plan">
            <BookOpenTextIcon size={16} />
            Lesson Plan
          </ContentPanelTabsTrigger>
          <ContentPanelTabsTrigger value="worksheet">
            <NotebookIcon size={16} />
            Learner Worksheet
          </ContentPanelTabsTrigger>
          <ContentPanelTabsTrigger value="game-level">
            <JoystickIcon size={16} />
            Game Level
            {!isCustomLevelsEnabled && <ProIcon />}
          </ContentPanelTabsTrigger>
          <ContentPanelTabsTrigger value="present">
            <PresentationIcon size={16} />
            Present
          </ContentPanelTabsTrigger>
        </TabsList>
        {activeTab === 'game-level' && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <CustomLevelEditorTour />
          </div>
        )}
      </TabsListContainer>

      <TabsContent value="lesson-plan" className="flex-1 overflow-auto">
        <LessonPlanContainer>
          <EditorContainer>
            <LessonPlanEditor
              content={content}
              onContentChange={onContentChange}
              selectedSectionId={selectedSectionId}
              onSelectedSectionIdChange={onSelectedSectionIdChange}
            />
          </EditorContainer>
          <ControlPanelWrapper>
            <ControlPanel
              content={content}
              onContentChange={onContentChange}
              selectedSectionId={selectedSectionId}
              onSelectedSectionIdChange={onSelectedSectionIdChange}
            />
          </ControlPanelWrapper>
        </LessonPlanContainer>
      </TabsContent>

      <TabsContent value="worksheet" className="flex-1 overflow-auto">
        <WorksheetEditor
          content={content}
          worksheetContent={worksheetContent}
          onWorksheetContentChange={onWorksheetContentChange}
          outOfSync={worksheetOutOfSync}
          onRegenerate={onWorksheetRegenerate}
        />
      </TabsContent>

      <TabsContent value="game-level" className="flex-1 overflow-auto">
        <LessonPlanGameLevel
          lessonPlanId={lessonPlanId}
          content={content}
          lessonPlanName={lessonPlanName ?? ''}
          outOfSync={gameLevelOutOfSync}
          onDismissOutOfSync={onGameLevelSync}
          setIsSaving={setIsGameLevelSaving}
          setLastSaved={setGameLevelLastSaved}
        />
      </TabsContent>

      <TabsContent value="present" className="flex-1 overflow-hidden">
        <Presenter content={content} />
      </TabsContent>
    </Tabs>
  );
}

const TabsListContainer = styled.div`
  padding: 4px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid hsl(var(--border));
`;

const LessonPlanContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const EditorContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  background-color: #f1f3f4;
`;

const ControlPanelWrapper = styled.div`
  width: 400px;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  border-left: 1px solid hsl(var(--border));
`;
